import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Survey } from "../api/types";
import { ConditionPill } from "../components/Badges";
import { KpiTile } from "../components/Charts";
import { DataTable } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { Field, PageHeader, Panel } from "../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../components/States";
import { km, num } from "../lib/format";
import { uniq, useUrlState } from "../lib/useUrlState";

const FILTERS = ["q", "agency", "county", "surface", "condition", "from", "to"] as const;

function toCsv(rows: Survey[]) {
  const head = ["Road", "Name", "Agency", "County", "Surface", "Length (km)", "Condition", "Verified findings", "Unreviewed AI findings", "Images", "Survey date"];
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.road_code, r.road_name, r.agency, r.county, r.surface, r.length_km, r.condition, r.findings?.verified, r.findings?.unreviewed, r.images, r.survey_date].map(esc).join(","));
  return [head.map(esc).join(","), ...body].join("\n");
}

export function RoadsPage() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["roads"], queryFn: () => api<{ items: Survey[] }>("/api/roads") });
  const f = useUrlState(FILTERS);

  const all = q.data?.items ?? [];
  const options = useMemo(() => ({
    agency: uniq(all.map((r) => r.agency)),
    county: uniq(all.map((r) => r.county)),
    surface: uniq(all.map((r) => r.surface)),
  }), [all]);

  const rows = useMemo(() => {
    const t = f.values.q.trim().toLowerCase();
    return all.filter((r) =>
      (!t || [r.road_code, r.road_name, r.county, r.agency, r.start_name, r.end_name].join(" ").toLowerCase().includes(t)) &&
      (!f.values.agency || r.agency === f.values.agency) &&
      (!f.values.county || r.county === f.values.county) &&
      (!f.values.surface || r.surface === f.values.surface) &&
      (!f.values.condition || r.condition === f.values.condition) &&
      (!f.values.from || r.survey_date >= f.values.from) &&
      (!f.values.to || r.survey_date <= f.values.to));
  }, [all, f.values]);

  const total = rows.reduce((s, r) => s + r.length_km, 0);
  const band = (b: string) => rows.filter((r) => r.condition === b).reduce((s, r) => s + r.length_km, 0);

  const exportCsv = () => {
    const blob = new Blob(["﻿" + toCsv(rows)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `RoadricsAI_roads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const select = (id: string, label: string, opts: string[]) => (
    <Field label={label} htmlFor={`f-${id}`}>
      <select id={`f-${id}`} className="select" value={f.values[id as (typeof FILTERS)[number]]} onChange={(e) => f.set(id as (typeof FILTERS)[number], e.target.value)}>
        <option value="">All</option>
        {opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    </Field>
  );

  return (
    <>
      <PageHeader title="Roads" subtitle="Every completed survey you have access to." actions={<button className="btn" onClick={exportCsv} disabled={!rows.length}><Icon name="download" />Export list</button>} />

      <div className="filters" role="search" aria-label="Filter roads">
        <Field label="Search" htmlFor="f-q" grow>
          <input id="f-q" className="input" placeholder="Road code, name, county, landmark" value={f.values.q} onChange={(e) => f.set("q", e.target.value)} />
        </Field>
        {select("agency", "Agency", options.agency)}
        {select("county", "County", options.county)}
        {select("surface", "Surface", options.surface)}
        {select("condition", "Condition", ["Good", "Fair", "Poor", "Not rated"])}
        <Field label="Surveyed from" htmlFor="f-from"><input id="f-from" type="date" className="input" value={f.values.from} onChange={(e) => f.set("from", e.target.value)} /></Field>
        <Field label="to" htmlFor="f-to"><input id="f-to" type="date" className="input" value={f.values.to} onChange={(e) => f.set("to", e.target.value)} /></Field>
        {f.active ? <button className="btn btn-ghost" onClick={f.clear}>Clear filters</button> : null}
      </div>

      <QueryState query={q} skeleton={<SkeletonBlock rows={8} height={28} />}>
        {() => (
          <>
            <div className="kpis">
              <KpiTile icon="roads" label="Roads" value={num(rows.length)} />
              <KpiTile icon="map" label="Length" value={km(total)} />
              <KpiTile label="Good" tone="good" value={km(band("Good"))} />
              <KpiTile label="Fair" tone="fair" value={km(band("Fair"))} />
              <KpiTile label="Poor" tone="poor" value={km(band("Poor"))} />
            </div>
            <Panel flush>
              <DataTable<Survey>
                rows={rows}
                rowKey={(r) => r.survey_uuid}
                onRowClick={(r) => nav(`/roads/${r.survey_uuid}`)}
                initialSort={{ key: "date", dir: "desc" }}
                caption="Surveyed roads"
                empty={all.length
                  ? <EmptyState title="No roads match these filters" action={<button className="btn" onClick={f.clear}>Clear filters</button>} />
                  : <EmptyState title="No roads surveyed yet">Completed surveys appear here once they have synced.</EmptyState>}
                columns={[
                  { key: "code", label: "Road", sort: (r) => r.road_code, render: (r) => <Link className="row-link" to={`/roads/${r.survey_uuid}`} onClick={(e) => e.stopPropagation()}>{r.road_code}</Link> },
                  { key: "name", label: "Name", sort: (r) => r.road_name, render: (r) => <>{r.road_name}{r.start_name || r.end_name ? <small className="muted" style={{ display: "block" }}>{[r.start_name, r.end_name].filter(Boolean).join(" → ")}</small> : null}</> },
                  { key: "agency", label: "Agency", sort: (r) => r.agency, render: (r) => r.agency || "—" },
                  { key: "county", label: "County", sort: (r) => r.county, render: (r) => r.county || "—" },
                  { key: "surface", label: "Surface", sort: (r) => r.surface, render: (r) => r.surface || "—" },
                  { key: "len", label: "Length", align: "right", sort: (r) => r.length_km, render: (r) => km(r.length_km, 2) },
                  { key: "cond", label: "Condition", sort: (r) => r.condition, render: (r) => <ConditionPill value={r.condition} provisional={r.condition_provisional} /> },
                  { key: "find", label: "Findings", align: "right", sort: (r) => r.findings?.verified ?? 0, render: (r) => (
                    <span className="num" title={`${r.findings?.verified ?? 0} verified, ${r.findings?.unreviewed ?? 0} unreviewed`}>
                      {r.findings?.verified ?? 0}{r.findings?.unreviewed ? <span className="muted"> +{r.findings.unreviewed}</span> : null}
                    </span>) },
                  { key: "img", label: "Images", align: "right", sort: (r) => r.images, render: (r) => num(r.images) },
                  { key: "date", label: "Survey date", sort: (r) => r.survey_date, render: (r) => r.survey_date },
                ]}
              />
            </Panel>
            <p className="muted" style={{ fontSize: 13 }}>Findings: verified count, with unreviewed AI findings after the +. Unreviewed findings are not counted in totals.</p>
          </>
        )}
      </QueryState>
    </>
  );
}
